import { Link } from "react-router-dom";
import { useDashboard } from "../../hooks/useDashboard";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  planning: { label: "Planificación", className: "bg-purple-100 text-purple-700" },
  in_progress: { label: "En progreso", className: "bg-blue-100 text-blue-700" },
  on_hold: { label: "En espera", className: "bg-yellow-100 text-yellow-700" },
  completed: { label: "Completado", className: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelado", className: "bg-red-100 text-red-700" },
  pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
  approved: { label: "Aprobado", className: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazado", className: "bg-red-100 text-red-700" },
};

function StatusBadge({ status }: { status: string }) {
  const info = STATUS_LABELS[status] || {
    label: status,
    className: "bg-slate-100 text-slate-700",
  };
  return (
    <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${info.className}`}>
      {info.label}
    </span>
  );
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatRelativeTime(timestamp: number | null) {
  if (!timestamp) return "—";
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  if (hours < 24) return `Hace ${hours} h`;
  if (days < 7) return `Hace ${days} d`;
  return new Date(timestamp).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function OverviewPage() {
  const { client } = useAuth();
  const { data, isLoading, error, refresh } = useDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-slate-500 text-sm">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-start gap-3">
        <span className="text-lg">⚠️</span>
        <div className="flex-1">
          <p className="font-medium">{error}</p>
          <button
            onClick={refresh}
            className="mt-2 text-sm underline hover:no-underline font-semibold"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, recentProjects, recentAdvances, unreadMessages } = data;

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white rounded-2xl p-6">
        <h2 className="text-2xl font-bold">
          Bienvenido, {client?.name} 👋
        </h2>
        <p className="text-white/85 text-sm mt-1">
          Aquí tienes un resumen de tus proyectos
        </p>
        {unreadMessages > 0 && (
          <Link
            to="/dashboard/messages"
            className="inline-flex items-center gap-2 mt-4 bg-white/15 hover:bg-white/25 backdrop-blur px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            💬 Tienes {unreadMessages} mensaje{unreadMessages !== 1 ? "s" : ""} sin leer
          </Link>
        )}
        {data.pendingChangeOrders > 0 && (
            <Link
                to="/dashboard/changes"
                className="block bg-yellow-50 border border-yellow-300 rounded-2xl p-4 hover:shadow-md transition"
            >
                <div className="flex items-center gap-3">
                <span className="text-3xl">🔄</span>
                <div className="flex-1">
                    <p className="font-bold text-yellow-900">
                    Tienes {data.pendingChangeOrders} solicitud
                    {data.pendingChangeOrders !== 1 ? "es" : ""} de cambio pendiente
                    {data.pendingChangeOrders !== 1 ? "s" : ""} de tu aprobación
                    </p>
                    <p className="text-sm text-yellow-700">
                    Revisa y aprueba o rechaza las modificaciones propuestas.
                    </p>
                </div>
                <span className="text-2xl text-yellow-600">→</span>
                </div>
            </Link>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="📋"
          label="Proyectos activos"
          value={String(summary.activeProjects)}
        />
        <KpiCard
          icon="📈"
          label="Avance promedio"
          value={`${summary.averageProgress}%`}
        />
        <KpiCard
          icon="🪙"
          label="Tokens disponibles"
          value={String(summary.tokenBalance)}
        />
        <KpiCard
          icon="📄"
          label="Docs. por firmar"
          value={String(summary.pendingDocuments)}
          highlight={summary.pendingDocuments > 0}
        />
      </div>

      {/* Card de IA */}
        <Link
        to="/dashboard/models/new"
        className="block bg-gradient-to-r from-purple-600 to-primary text-white rounded-2xl p-5 hover:shadow-lg transition-shadow"
        >
        <div className="flex items-center justify-between gap-4">
            <div>
            <p className="text-lg font-bold">✨ Visualiza tu proyecto con IA</p>
            <p className="text-sm text-white/85 mt-1">
                Describe tu idea y obtén un modelo 3D en segundos (costo: 3 tokens)
            </p>
            </div>
            <span className="text-3xl shrink-0">→</span>
        </div>
        </Link>

      {/* Recent projects */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-900">Proyectos recientes</h3>
            <p className="text-xs text-slate-500 mt-0.5">Tus últimos proyectos activos</p>
          </div>
          <Link
            to="/dashboard/projects"
            className="text-sm text-primary hover:underline font-medium"
          >
            Ver todos →
          </Link>
        </div>
        <div className="p-5">
          {recentProjects.length === 0 ? (
            <EmptyState
              icon="🏗️"
              title="Aún no tienes proyectos"
              subtitle="Cuando el despacho te asigne un proyecto, aparecerá aquí."
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {recentProjects.map((project) => (
                <li key={project.id} className="py-3 first:pt-0 last:pb-0">
                  <Link
                    to={`/dashboard/projects/${project.id}`}
                    className="flex items-center justify-between gap-4 hover:bg-slate-50 -mx-2 px-2 py-1 rounded-lg transition"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 truncate">
                          {project.name}
                        </p>
                        <StatusBadge status={project.status} />
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Presupuesto: {formatCurrency(project.budget)} • Último avance:{" "}
                        {formatRelativeTime(project.lastAdvanceAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="hidden sm:block w-32">
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${Math.min(project.progress, 100)}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-bold text-primary w-12 text-right">
                        {project.progress}%
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent advances */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-900">Últimos avances</h3>
            <p className="text-xs text-slate-500 mt-0.5">Reportes recientes de tus proyectos</p>
          </div>
          <Link
            to="/dashboard/advances"
            className="text-sm text-primary hover:underline font-medium"
          >
            Ver todos →
          </Link>
        </div>
        <div className="p-5">
          {recentAdvances.length === 0 ? (
            <EmptyState
              icon="📈"
              title="Sin avances aún"
              subtitle="Los avances semanales de tus proyectos aparecerán aquí."
            />
          ) : (
            <ul className="space-y-3">
              {recentAdvances.map((advance) => (
                <li
                  key={advance.id}
                  className="flex items-start justify-between gap-4 p-3 bg-slate-50 rounded-lg"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900 truncate">
                        {advance.projectName}
                      </p>
                      <StatusBadge status={advance.status} />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Semana {advance.weekNumber}/{advance.year} •{" "}
                      {formatRelativeTime(advance.createdAt)}
                    </p>
                    {advance.description && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                        {advance.description}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-lg font-bold text-primary">
                      +{advance.progressPercentage}%
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: string;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`bg-white rounded-2xl border p-4 shadow-sm ${
        highlight ? "border-primary/30" : "border-slate-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${highlight ? "text-primary" : "text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  subtitle,
}: {
  icon: string;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="text-center py-8">
      <div className="text-4xl mb-3 opacity-40">{icon}</div>
      <p className="font-medium text-slate-700">{title}</p>
      <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
    </div>
  );
}